import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
} from "axios";
import i18n from "i18n/i18n";
import qs from "qs";
import { toast } from "shared/components/ui/use-toast";
import {
  auth,
  environment,
  Environment,
  toApiHost,
} from "shared/config/firebase";
import JwtAuthService from "./JwtAuthService";
import FirebaseTokenService from "./FirebaseTokenService";

interface RestResponse<T> {
  resource?: T;
  error?: any;
}

const BACKEND_API_BASE_URL = window.__APP_CONFIG__?.BACKEND_API_BASE_URL;

export class ApiResponseError extends Error {
  readonly data?: any;
  readonly status: number;
  readonly cause: Error;
  readonly request: any;

  constructor(error: AxiosError, status: number, data?: any) {
    const msg =
      data?.error && data.error?.error_message
        ? data.error.error_message
        : data?.error?.message
          ? data.error.message
          : error.message;
    super(msg);
    this.name = "ApiResponseError";
    this.cause = error;
    this.status = status;
    this.data = data;
    this.request = error?.request;
  }
}

class RestService {
  private ENVIRONMENT_APIS: { [key in Environment]: AxiosInstance } = {
    "streetbeat-production": this.createAPIInstance("streetbeat-production"),
    "streetbeat-staging": this.createAPIInstance("streetbeat-staging"),
    "from-host": this.createAPIInstance("from-host"),
  };

  private ENVIRONMENT_APIS_FORM_DATA: { [key in Environment]: AxiosInstance } =
    {
      "streetbeat-production": this.createAPIInstance(
        "streetbeat-production",
        "multipart/form-data",
      ),
      "streetbeat-staging": this.createAPIInstance(
        "streetbeat-staging",
        "multipart/form-data",
      ),
      "from-host": this.createAPIInstance("from-host", "multipart/form-data"),
    };

  private createAPIInstance(
    environment: Environment,
    contentType: string = "application/json",
  ) {
    let baseUrl = BACKEND_API_BASE_URL || `https://${toApiHost()}`;


    const instance = axios.create({
      baseURL: baseUrl,
    });
    this.addRequestInterceptor(instance, contentType);
    this.addResponseInterceptor(instance);
    return instance;
  }

  private async addRequestInterceptor(
    instance: AxiosInstance,
    contentType: string | null = "application/json",
  ) {
    instance.interceptors.request.use(
      async (config) => {
        // Inject standard headers
        config.headers.Accept = "application/json";
        config.headers["Content-Type"] = contentType || "application/json";
        config.headers["AppVersionEpoch"] = "99999999999";
        config.headers["Domain"] = window.location.host;

        // Check if JWT authentication should be used
        if (JwtAuthService.shouldUseJwtAuth()) {
          const jwtToken = JwtAuthService.getToken();
          if (jwtToken) {
            config.headers["Jwt-Jwks"] = jwtToken;
            // Return early to avoid adding Firebase token
            return config;
          }
        }

        // Inject firebase token (only if JWT auth is not used)
        try {
          await auth.authStateReady();
          const token = await FirebaseTokenService.getCurrentToken();
          if (token) {
            config.headers.Firebase = token;
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.warn("[RestService] request", JSON.stringify(error));
        }
        return config;
      },
      (error) => Promise.reject(error),
    );
  }

  private addResponseInterceptor(instance: AxiosInstance) {
    instance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response && error.response.status === 401) {
          if (JwtAuthService.shouldUseJwtAuth()) {
            JwtAuthService.clearAuth();
            window.location.href = "/jwt-auth-required";
            return Promise.reject(new Error("Authentication required"));
          }
        }

        if (!error.response) {
          console.warn("[RestService] response", JSON.stringify(error));
          const apiError = new ApiResponseError(error, 0, null);
          if (![401, 404].includes(error.request.status)) {
            toast({
              description: apiError.message,
              title: i18n.t("global.error"),
              variant: "destructive",
            });
          }
          return Promise.reject(apiError);
        }

        const { status, data } = error.response;
        const apiError = new ApiResponseError(error, status, data);
        if (![401, 404].includes(error.request.status)) {
          toast({
            description: apiError.message,
            title: i18n.t("global.error"),
            variant: "destructive",
          });
        }
        return Promise.reject(apiError);
      },
    );
  }

  async api(): Promise<AxiosInstance> {
    const env = await environment();
    if (env === "from-host") {
      return this.ENVIRONMENT_APIS["from-host"];
    }
    if (!auth.currentUser) return this.ENVIRONMENT_APIS["production"];
    if (!auth.currentUser.email) return this.ENVIRONMENT_APIS["production"];

    return this.ENVIRONMENT_APIS[env];
  }

  async apiFormData(): Promise<AxiosInstance> {
    const env = await environment();
    if (env === "from-host") {
      return this.ENVIRONMENT_APIS_FORM_DATA["from-host"];
    }
    if (!auth.currentUser) return this.ENVIRONMENT_APIS_FORM_DATA["production"];
    if (!auth.currentUser.email)
      return this.ENVIRONMENT_APIS_FORM_DATA["production"];

    return this.ENVIRONMENT_APIS_FORM_DATA[env];
  }

  async post<T>(url: string, payload?: any, params?: any): Promise<T> {
    const awaitedApi = await this.api();
    const { data } = await awaitedApi.post<RestResponse<T>>(
      url,
      payload,
      params,
    );
    return data?.resource || (data as T);
  }

  async postFormData<T>(
    url: string,
    payload: FormData,
    params?: any,
  ): Promise<T> {
    const awaitedApi = await this.apiFormData();
    const { data } = await awaitedApi.post<RestResponse<T>>(
      url,
      payload,
      params,
    );
    return data?.resource || (data as T);
  }

  async get<T>(url: string, params?: {}): Promise<T> {
    const awaitedApi = await this.api();
    const { data } = await awaitedApi.get<RestResponse<T>>(url, {
      params,
      paramsSerializer: (serializedParams) =>
        qs.stringify(serializedParams, { arrayFormat: "repeat" }),
    });
    return data?.resource || (data as T);
  }

  async put<T>(url: string, payload?: any, params?: any): Promise<T> {
    const awaitedApi = await this.api();
    const { data } = await awaitedApi.put<RestResponse<T>>(url, payload, {
      params,
    });
    return data?.resource || (data as T);
  }

  async patch<T>(url: string, payload?: any, params?: any): Promise<T> {
    const awaitedApi = await this.api();
    const { data } = await awaitedApi.patch<RestResponse<T>>(
      url,
      payload,
      params,
    );
    return data?.resource || (data as T);
  }

  async delete<T>(url: string, params?: any): Promise<T> {
    const awaitedApi = await this.api();
    const { data } = await awaitedApi.delete<RestResponse<T>>(url, { params });
    return data?.resource || (data as T);
  }

  async download(url: string): Promise<AxiosResponse<any, any>> {
    const awaitedApi = await this.api();
    const response = await awaitedApi.get<any>(url, { responseType: "blob" });
    return response;
  }


  private createBaseAPIInstance(environment: Environment) {
    let baseUrl: string;
    if (environment === "from-host") {
      baseUrl = `https://${toApiHost()}`;
    } else {
      baseUrl = BACKEND_API_BASE_URL || `https://${toApiHost()}`;
    }


    const instance = axios.create({
      baseURL: baseUrl,
    });
    this.addRequestInterceptor(instance);
    this.addResponseInterceptor(instance);
    return instance;
  }
  private ENVIRONMENT_BASE_APIS: { [key in Environment]: AxiosInstance } = {
    "streetbeat-production": this.createBaseAPIInstance(
      "streetbeat-production",
    ),
    "streetbeat-staging": this.createBaseAPIInstance("streetbeat-staging"),
    "from-host": this.createBaseAPIInstance("from-host"),
  };
  private async detectEnvironmentFromCurrentUser(): Promise<Environment> {
    return await environment();
  }
  async baseApi(): Promise<AxiosInstance> {
    const env = await this.detectEnvironmentFromCurrentUser();
    return this.ENVIRONMENT_BASE_APIS[env];
  }

  async postBaseDownload(
    url: string,
    payload?: any,
  ): Promise<AxiosResponse<any, any>> {
    const awaitedApi = await this.baseApi();
    const response = await awaitedApi.post<any>(url, payload, {
      responseType: "blob",
    });
    return response;
  }
}

const restService = new RestService();
export default restService;
